import { BehaviorSubject } from 'rxjs';
import { BaseService } from './base.service';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ICampaign } from '../interfaces/i-campaign';
import { IRestaurant } from '../interfaces/i-restaurant';
import { isPlatformBrowser } from '@angular/common';
import { SLUGS } from '../utils/slugs';
import { ICategory } from '../interfaces/i-category';
import { CATEGORIES } from '../utils/categories';
import { MOMENTS } from '../utils/moments';
import { IMoment } from '../interfaces/i-moment';
import { IEvent } from '../interfaces/i-events';

@Injectable({
  providedIn: 'root',
})
export class WebappService {
  public campaigns: BehaviorSubject<ICampaign[]> = new BehaviorSubject(
    [] as ICampaign[]
  );
  public restaurants: BehaviorSubject<IRestaurant[]> = new BehaviorSubject(
    [] as IRestaurant[]
  );
  public events: BehaviorSubject<IEvent[]> = new BehaviorSubject(
    [] as IEvent[]
  );
  public categories: BehaviorSubject<ICategory[]> = new BehaviorSubject(
    CATEGORIES as ICategory[]
  );
  public moments: BehaviorSubject<IMoment[]> = new BehaviorSubject(
    MOMENTS as IMoment[]
  );

  public selectedCity: BehaviorSubject<string> = new BehaviorSubject('4314902');
  public isLoading = new BehaviorSubject(false);
  public isBrowser: boolean = false;

  constructor(
    private baseService: BaseService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  public getCities() {
    if (this.isBrowser) {
      return this.baseService.get('cities');
    } else {
      return this.baseService.getCities();
    }
  }

  getCampaign(campaignId: string) {
    if (this.isBrowser) {
      return this.baseService.get(`campaign?id=${campaignId}`);
    } else {
      return this.baseService.getCampaign(campaignId);
    }
  }

  getCampains(cityId: string) {
    if (this.isBrowser) {
      this.baseService.get(`campaigns?cityId=${cityId}`).then((result) => {
        this.campaigns.next(result as ICampaign[]);
      });
    } else {
      this.baseService.getCampaigns(cityId).then((result) => {
        this.campaigns.next(result as ICampaign[]);
      });
    }
  }

  searchCampaigns(categoriesId: string = '', mommentId: string = '') {
    this.isLoading.next(true);
    let cityId = this.selectedCity.value;
    let query = '';
    if (categoriesId) {
      query += `&categorieId=${categoriesId}`;
    }
    if (mommentId) {
      query += `&mommentId=${mommentId}`;
    }
    this.baseService
      .getCampaigns(`${cityId}${query}`)
      .then((result) => {
        this.campaigns.next(result as ICampaign[]);
      })
      .finally(() => this.isLoading.next(false));
  }

  getCategory(cityId: string, categoryId: string) {
    if (this.isBrowser) {
      return this.baseService.get(`category?cityId=${cityId}&id=${categoryId}`);
    } else {
      return this.baseService.getCampaignsByCategory(cityId, categoryId);
    }
  }

  getRestaurant(restaurantId: string) {
    if (this.isBrowser) {
      return this.baseService.get(`restaurant?id=${restaurantId}`);
    } else {
      return this.baseService.getRestaurant(restaurantId);
    }
  }

  getRestaurantBySlug(restaurantSlug: string) {
    if (this.isBrowser) {
      return this.baseService.get(`restaurantBySlug?slug=${restaurantSlug}`);
    } else {
      return this.baseService.getRestaurantBySlug(restaurantSlug);
    }
  }

  getRestaurantByCity(cityId: string) {
    if (this.isBrowser) {
      this.baseService.get(`restaurants?cityId=${cityId}`).then((result) => {
        this.restaurants.next(result as IRestaurant[]);
      });
    } else {
      this.baseService.getRestaurants(cityId).then((result) => {
        this.restaurants.next(result as IRestaurant[]);
      });
    }
  }

  getFAQ() {
    if (this.isBrowser) {
      return this.baseService.get('faq');
    }
    return this.baseService.getFAQ();
  }

  getEvents() {
    if (this.isBrowser) {
      return this.baseService.getPascalCase('events').then((result) => {
        this.events.next(result as IEvent[]);
      });
    } else {
      return this.baseService.getEvents().then((result) => {
        this.events.next(result as IEvent[]);
      }).catch(error => console.log(error));
    }
  }

  getCampaignsByEvent(campaignEventId: string | null) {
    if(!campaignEventId) return;
    if (this.isBrowser) {
      return this.baseService.getPascalCase(`event?id=${campaignEventId}`);
    } else {
      return this.baseService.getCampaignsByEvent(campaignEventId);
    }
  }

  createSlugs(cityId: string, restaurants: IRestaurant[]) {
    if (!this.isBrowser) {
      return;
    }
    const slugs = {} as any;
    slugs[cityId] = {};
    for (let res of restaurants) {
      const {
        Id,
        TradeName,
        Description,
        LogoImagePath,
        Address,
        ShowcaseImagePath,
      } = res;
      slugs[cityId][res.SluggedTradeName] = {
        Id,
        TradeName,
        Description,
        LogoImagePath,
        Address,
        ShowcaseImagePath,
      };
    }

    SLUGS[cityId] = slugs[cityId];
    SLUGS['all'] = { ...SLUGS['all'], ...slugs[cityId] };

    this.saveSlugs();
  }

  saveSlugs() {
    localStorage.setItem('slugs', JSON.stringify(SLUGS));
  }
}
